import React, { useState } from 'react';
import { Modal } from '@/components/feedback/modal';
import { Button } from '@/components/display/button';
import { AlertTriangle, Check, Copy } from 'lucide-react';
import { toast } from 'react-toastify';
import { clsx } from 'clsx';

type Props = {
  apiKey: string | null;
  onClose: () => void;
};

const toastProps = {
  autoClose: 2000,
  closeOnClick: true,
  style: { width: 'auto', padding: '10px 20px' }
};

export function ApiKeyCreatedModal({ apiKey, onClose }: Props) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!apiKey) return;
    try {
      await navigator.clipboard.writeText(apiKey);
      setCopied(true);
      toast.success('API key copied to clipboard', toastProps);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy to clipboard', toastProps);
    }
  };

  const handleClose = () => {
    setCopied(false);
    onClose();
  };

  if (!apiKey) return null;

  return (
    <Modal
      open={!!apiKey}
      setOpen={open => {
        if (!open) handleClose();
      }}
      header={
        <div className="flex flex-col items-center gap-2 rounded-t-2xl bg-amber-100 py-6">
          <AlertTriangle className="h-10 w-10 text-amber-600" />
          <h2 className="text-center text-xl font-semibold text-amber-800">API Key Created</h2>
        </div>
      }
      content={
        <div className="px-6 py-4">
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
            <p className="mb-3 text-sm font-medium text-amber-800">
              Make sure to copy your API key now. You won't be able to see it again!
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 overflow-x-auto rounded bg-gray-900 px-3 py-2 font-mono text-sm text-gray-100">
                {apiKey}
              </code>
              <button
                onClick={handleCopy}
                className={clsx(
                  'flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md transition-colors',
                  copied
                    ? 'bg-lime-100 text-lime-600'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                )}>
                {copied ? <Check className="h-5 w-5" /> : <Copy className="h-5 w-5" />}
              </button>
            </div>
          </div>
          <p className="mt-4 text-sm text-muted-foreground">
            Store this key securely. For security reasons, we don't store the full key and cannot
            show it again.
          </p>
        </div>
      }
      footer={
        <div className="border-t px-6 py-4">
          <Button className="w-full justify-center" onClick={handleClose}>
            I've copied my key
          </Button>
        </div>
      }
    />
  );
}
